import { useState, useEffect, useRef, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './i18n';
import i18n from './i18n';
import { setupVisitPing } from './visitPing';
import '@/App.css';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Shield, Zap, RotateCcw, Settings, LogOut, Download, CheckCircle, AlertTriangle, Copy, CheckCheck, FileCode, Lock, Mail, KeyRound, ArrowLeft, Eye, EyeOff } from 'lucide-react';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Persönliche Bewerbungslinks (cleanstart.ch/?r=firma) beim Backend melden
setupVisitPing(`${API}/visit`, 'cleanstart');

// Setup Axios interceptor for global 401 handling
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      const token = localStorage.getItem('token');
      if (token) {
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
        // Toast will be shown by the component that catches the error
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth Context
const AuthContext = createContext(null);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [initializing, setInitializing] = useState(true);
  const [serverStatus, setServerStatus] = useState('checking'); // 'checking' | 'starting' | 'ready'

  const [recipesCache, setRecipesCache] = useState(null);

  useEffect(() => {
    initializeApp();
  }, []);

  const preloadData = async () => {
    try {
      const response = await axios.get(`${API}/recipes?lang=${i18n.language}`);
      setRecipesCache(response.data);
    } catch (err) {
      console.error('Preload recipes failed', err);
    }
  };

  const initializeApp = async () => {
    // Parallel: fast health ping (5s) to detect if server is awake
    const healthPing = axios.get(`${API}/health`, { timeout: 5000 })
      .then(() => {
        setServerStatus('ready');
        // Preload recipes once server is confirmed ready
        preloadData();
      })
      .catch(() => setServerStatus('starting'));

    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      try {
        // Main auth call with 60s timeout for cold starts
        const response = await axios.get(`${API}/auth/me`, { timeout: 60000 });
        setUser(response.data);
        setServerStatus('ready');
      } catch (error) {
        if (error.response?.status === 401) {
          // Invalid/expired token - clear it
          localStorage.removeItem('token');
          setToken(null);
          delete axios.defaults.headers.common['Authorization'];
        }
        // For network/timeout errors, user stays null → redirected to login
      }
    } else {
      // No token - just wait for health ping to finish
      await healthPing;
    }

    setInitializing(false);
  };

  const login = async (newToken) => {
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    try {
      const response = await axios.get(`${API}/auth/me`, { timeout: 45000 });
      setUser(response.data);
    } catch (err) {
      console.error('Failed to fetch user after login', err);
    }
    setToken(newToken);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, initializing, serverStatus, recipesCache }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

// Loading Screen Component
const LoadingScreen = ({ message, subtitle = null }) => {
  const { t } = useTranslation();
  const displayMessage = message || t('loading.default');
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 95) return prev;
        return prev + Math.random() * 2;
      });
    }, 800);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="loading-screen-full" data-testid="loading-screen">
      <div className="loading-content">
        <div className="loading-icon">
          <Settings className="rotating-icon" size={64} />
        </div>
        <h2 className="loading-title">{displayMessage}</h2>
        {subtitle && <p className="loading-subtitle">{subtitle}</p>}
        
        <div className="progress-bar-container">
          <div 
            className="progress-bar-fill" 
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>
        <p className="progress-text">{Math.floor(progress)}%</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, initializing } = useAuth();

  if (initializing) return null; // Global LoadingScreen handles this
  if (!user) return <Navigate to="/login" />;
  if (adminOnly && !user.is_admin) return <Navigate to="/" />;

  return children;
};

// Navigation
const Navigation = () => {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();

  const toggleLanguage = () => {
    const newLang = i18n.language === 'de-CH' ? 'en-US' : 'de-CH';
    i18n.changeLanguage(newLang);
  };

  return (
    <nav className="nav-bar" data-testid="main-navigation">
      <div className="nav-container">
        <Link to="/" className="nav-logo" data-testid="nav-logo">
          <img src="/logo-mark.svg" alt="" className="nav-logo-icon" width="36" height="36" />
          <span>CleanStart</span>
        </Link>
        <div className="nav-links">
          <Link to="/" data-testid="nav-home">{t('nav.home')}</Link>
          {user && (
            <>
              <Link to="/wizard" data-testid="nav-wizard">{t('nav.wizard')}</Link>
              {user.is_admin && <Link to="/admin" data-testid="nav-admin">{t('nav.admin')}</Link>}
            </>
          )}
        </div>
        <div className="nav-actions">
          <Button variant="ghost" onClick={toggleLanguage} size="sm" data-testid="language-toggle">
            {i18n.language === 'de-CH' ? '🇬🇧 EN' : '🇨🇭 DE'}
          </Button>
          {user ? (
            <Button variant="ghost" onClick={logout} size="sm" data-testid="logout-button">
              <LogOut className="w-4 h-4 mr-2" />
              {t('nav.logout')}
            </Button>
          ) : (
            <Link to="/login">
              <Button size="sm" data-testid="login-button">{t('nav.login')}</Button>
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
};
// Footer
const Footer = () => {
  const { t } = useTranslation();
  
  return (
    <footer className="app-footer" data-testid="app-footer">
      <div className="footer-container">
        <div className="footer-links">
          <Link to="/eula">{t('footer.eula')}</Link>
          <span className="footer-divider">·</span>
          <Link to="/privacy">{t('footer.privacy')}</Link>
        </div>
        <p className="footer-copyright">{t('footer.copyright')}</p>
      </div>
    </footer>
  );
};
// Landing Page
const LandingPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="landing-page" data-testid="landing-page">
      <section className="hero-section">
        <div className="hero-content">
          <img src="/logo-mark.svg" alt="" className="hero-logo" width="96" height="96" />
          <h1 className="hero-title" data-testid="hero-title">{t('landing.hero.title')}</h1>
          <p className="hero-subtitle" data-testid="hero-subtitle">{t('landing.hero.subtitle')}</p>
          <Button 
            onClick={() => navigate(user ? '/wizard' : '/login')} 
            size="lg" 
            className="hero-cta"
            data-testid="hero-cta-button"
          >
            {t('landing.hero.cta')}
          </Button>
        </div>
      </section>

      <section className="features-section">
        <h2 className="features-title" data-testid="features-title">{t('landing.features.title')}</h2>
        <div className="features-grid">
          <Card className="feature-card" data-testid="feature-bloatware">
            <CardHeader>
              <Shield className="feature-icon" />
              <CardTitle>{t('landing.features.bloatware')}</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription>{t('landing.features.bloatware.desc')}</CardDescription>
            </CardContent>
          </Card>

          <Card className="feature-card" data-testid="feature-privacy">
            <CardHeader>
              <Shield className="feature-icon" />
              <CardTitle>{t('landing.features.privacy')}</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription>{t('landing.features.privacy.desc')}</CardDescription>
            </CardContent>
          </Card>

          <Card className="feature-card" data-testid="feature-performance">
            <CardHeader>
              <Zap className="feature-icon" />
              <CardTitle>{t('landing.features.performance')}</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription>{t('landing.features.performance.desc')}</CardDescription>
            </CardContent>
          </Card>

          <Card className="feature-card" data-testid="feature-rollback">
            <CardHeader>
              <RotateCcw className="feature-icon" />
              <CardTitle>{t('landing.features.rollback')}</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription>{t('landing.features.rollback.desc')}</CardDescription>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

// OTP Input Component (shared between Verify and Reset)
const OtpInput = ({ code, setCode, onComplete }) => {
  const inputRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);

    if (value && index < 5) {
      inputRefs[index + 1].current?.focus();
    }
    if (value && index === 5) {
      const fullCode = [...newCode];
      fullCode[5] = value.slice(-1);
      if (fullCode.every(d => d !== '')) {
        onComplete?.(fullCode.join(''));
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const newCode = [...code];
    for (let i = 0; i < pasted.length; i++) {
      newCode[i] = pasted[i];
    }
    setCode(newCode);
    const focusIdx = Math.min(pasted.length, 5);
    inputRefs[focusIdx].current?.focus();
    if (pasted.length === 6) onComplete?.(pasted);
  };

  return (
    <div className="otp-container" data-testid="otp-container">
      {code.map((digit, idx) => (
        <input
          key={idx}
          ref={inputRefs[idx]}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(idx, e.target.value)}
          onKeyDown={(e) => handleKeyDown(idx, e)}
          onPaste={idx === 0 ? handlePaste : undefined}
          className={`otp-input ${digit ? 'filled' : ''}`}
          data-testid={`otp-input-${idx}`}
          autoFocus={idx === 0}
        />
      ))}
    </div>
  );
};

// Login Page
const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      await login(response.data.access_token);
      toast.success(t('toast.login.success'));
      navigate('/wizard');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(t('toast.login.unverified'));
        navigate('/verify', { state: { email } });
      } else {
        toast.error(error.response?.data?.detail || t('toast.login.failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page" data-testid="login-page">
      <Card className="auth-card">
        <CardHeader className="text-center">
          <div className="auth-header-icon">
            <div className="icon-circle">
              <Mail size={24} />
            </div>
          </div>
          <CardTitle data-testid="login-title">{t('auth.login.title')}</CardTitle>
          <CardDescription>{t('auth.login.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <Label htmlFor="login-email">{t('auth.email')}</Label>
              <Input
                id="login-email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.email.placeholder')}
                required
              />
            </div>
            <div className="form-group">
              <Label htmlFor="login-password">{t('auth.password')}</Label>
              <div className="password-field">
                <Input
                  id="login-password"
                  data-testid="login-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="login-password-toggle"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
              {loading ? t('common.loading') : t('auth.login.button')}
            </Button>
          </form>
          <div className="auth-links">
            <Link to="/reset-password" className="auth-link" data-testid="forgot-password-link">
              {t('auth.forgot')}
            </Link>
            <div className="auth-divider" />
            <Link to="/register" className="auth-link" data-testid="switch-to-register">
              {t('auth.switch.register')}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Register Page
const RegisterPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error(t('toast.register.passwordMin'));
      return;
    }
    if (password !== confirmPassword) {
      toast.error(t('toast.register.passwordMismatch'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/register`, { email, password });
      toast.success(t('toast.register.success'));
      navigate('/verify', { state: { email } });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toast.register.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page" data-testid="register-page">
      <Card className="auth-card">
        <CardHeader className="text-center">
          <div className="auth-header-icon">
            <div className="icon-circle">
              <Shield size={24} />
            </div>
          </div>
          <CardTitle data-testid="register-title">{t('auth.register.title')}</CardTitle>
          <CardDescription>{t('auth.register.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <Label htmlFor="register-email">{t('auth.email')}</Label>
              <Input
                id="register-email"
                data-testid="register-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.email.placeholder')}
                required
              />
            </div>
            <div className="form-group">
              <Label htmlFor="register-password">{t('auth.password')}</Label>
              <div className="password-field">
                <Input
                  id="register-password"
                  data-testid="register-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={8}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="register-password-toggle"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <span className="password-hint">{t('auth.password.min')}</span>
            </div>
            <div className="form-group">
              <Label htmlFor="register-confirm">{t('auth.password.confirm')}</Label>
              <Input
                id="register-confirm"
                data-testid="register-confirm-input"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                minLength={8}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-button">
              {loading ? t('common.loading') : t('auth.register.button')}
            </Button>
          </form>
          <div className="auth-links">
            <Link to="/login" className="auth-link" data-testid="switch-to-login">
              {t('auth.switch.login')}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Verify Page (6 individual OTP inputs)
const VerifyPage = () => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { t } = useTranslation();
  const email = location.state?.email || '';

  useEffect(() => {
    if (!email) {
      navigate('/login');
    }
  }, [email, navigate]);

  const handleSubmit = async (codeStr) => {
    const verificationCode = codeStr || code.join('');
    if (verificationCode.length !== 6) {
      toast.error(t('toast.verify.incomplete'));
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify`, {
        email,
        code: verificationCode
      });
      await login(response.data.access_token);
      toast.success(t('toast.verify.success'));
      navigate('/wizard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toast.verify.failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-code`, { email });
      toast.success(t('auth.verify.resent'));
      setCode(['', '', '', '', '', '']);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toast.resend.failed'));
    } finally {
      setResending(false);
    }
  };

  if (!email) return null;

  return (
    <div className="auth-page" data-testid="verify-page">
      <Card className="auth-card">
        <CardHeader className="text-center">
          <div className="auth-header-icon">
            <div className="icon-circle">
              <KeyRound size={24} />
            </div>
          </div>
          <CardTitle data-testid="verify-title">{t('auth.verify.title')}</CardTitle>
          <CardDescription>
            {t('auth.verify.subtitle')}
          </CardDescription>
          <div className="verify-email" data-testid="verify-email-display">{email}</div>
          <div className="verify-spam-hint" data-testid="verify-spam-hint">
            {t('auth.verify.spamHint')}
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="auth-form">
            <OtpInput code={code} setCode={setCode} onComplete={handleSubmit} />
            <Button type="submit" className="w-full" disabled={loading} data-testid="verify-submit-button">
              {loading ? t('common.loading') : t('auth.verify.button')}
            </Button>
          </form>
          <div className="auth-links">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResend}
              disabled={resending}
              data-testid="resend-code-button"
            >
              {resending ? t('common.loading') : t('auth.verify.resend')}
            </Button>
            <div className="auth-divider" />
            <Link to="/login" className="auth-link" data-testid="back-to-login">
              <ArrowLeft size={14} style={{ display: 'inline', marginRight: 4 }} />
              {t('auth.reset.back')}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Reset Password Page (Multi-step)
const ResetPasswordPage = () => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      toast.success(t('toast.reset.codeSent'));
      setStep(2);
    } catch (error) {
      toast.error(t('toast.reset.sendFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = (e) => {
    e.preventDefault();
    if (code.join('').length !== 6) {
      toast.error(t('toast.verify.incomplete'));
      return;
    }
    setStep(3);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error(t('toast.register.passwordMin'));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t('toast.register.passwordMismatch'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email,
        code: code.join(''),
        new_password: newPassword
      });
      toast.success(t('auth.reset.success'));
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toast.reset.failed'));
    } finally {
      setLoading(false);
    }
  };

  const StepIndicator = () => (
    <div className="reset-steps" data-testid="reset-steps">
      <div className={`reset-step ${step >= 1 ? (step > 1 ? 'done' : 'active') : ''}`}>1</div>
      <div className={`reset-step-line ${step > 1 ? 'done' : ''}`} />
      <div className={`reset-step ${step >= 2 ? (step > 2 ? 'done' : 'active') : ''}`}>2</div>
      <div className={`reset-step-line ${step > 2 ? 'done' : ''}`} />
      <div className={`reset-step ${step >= 3 ? 'active' : ''}`}>3</div>
    </div>
  );

  return (
    <div className="auth-page" data-testid="reset-password-page">
      <Card className="auth-card">
        <CardHeader className="text-center">
          <div className="auth-header-icon">
            <div className="icon-circle">
              <KeyRound size={24} />
            </div>
          </div>
          <CardTitle data-testid="reset-title">
            {step === 1 && t('auth.reset.title')}
            {step === 2 && t('auth.reset.code.title')}
            {step === 3 && t('auth.reset.new.title')}
          </CardTitle>
          <CardDescription>
            {step === 1 && t('auth.reset.subtitle')}
            {step === 2 && t('auth.reset.code.subtitle')}
            {step === 3 && t('auth.reset.new.subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <StepIndicator />

          {step === 1 && (
            <form onSubmit={handleSendCode} className="auth-form">
              <div className="form-group">
                <Label htmlFor="reset-email">{t('auth.email')}</Label>
                <Input
                  id="reset-email"
                  data-testid="reset-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.email.placeholder')}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="reset-send-button">
                {loading ? t('common.loading') : t('auth.reset.send')}
              </Button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleVerifyCode} className="auth-form">
              <div className="verify-email" data-testid="reset-email-display">{email}</div>
              <OtpInput code={code} setCode={setCode} onComplete={() => setStep(3)} />
              <Button type="submit" className="w-full" data-testid="reset-verify-button">
                {t('auth.reset.step2.next')}
              </Button>
            </form>
          )}

          {step === 3 && (
            <form onSubmit={handleResetPassword} className="auth-form">
              <div className="form-group">
                <Label htmlFor="new-password">{t('auth.password.new')}</Label>
                <div className="password-field">
                  <Input
                    id="new-password"
                    data-testid="reset-new-password-input"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    minLength={8}
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <span className="password-hint">{t('auth.password.min')}</span>
              </div>
              <div className="form-group">
                <Label htmlFor="confirm-new-password">{t('auth.password.confirm')}</Label>
                <Input
                  id="confirm-new-password"
                  data-testid="reset-confirm-password-input"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  minLength={8}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="reset-submit-button">
                {loading ? t('common.loading') : t('auth.reset.button')}
              </Button>
            </form>
          )}

          <div className="auth-links">
            <Link to="/login" className="auth-link" data-testid="reset-back-to-login">
              <ArrowLeft size={14} style={{ display: 'inline', marginRight: 4 }} />
              {t('auth.reset.back')}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Category Badge Component (replaces old RiskBadge)
const CategoryBadge = ({ category }) => {
  const { t } = useTranslation();
  const categoryColors = {
    'datenschutz': 'bg-purple-100 text-purple-800 border-purple-300',
    'leistung': 'bg-blue-100 text-blue-800 border-blue-300',
    'komfort': 'bg-green-100 text-green-800 border-green-300'
  };
  
  return (
    <span className={`risk-badge ${categoryColors[category] || categoryColors['komfort']}`} data-testid={`category-badge-${category}`}>
      {t(`category.${category}`) || category}
    </span>
  );
};

// Wizard Page
const WizardPage = () => {
  const { recipesCache } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [selectedRecipes, setSelectedRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLang = i18n.language;

  useEffect(() => {
    fetchRecipes();
  }, [currentLang]);

  const fetchRecipes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/recipes?lang=${currentLang}`);
      setRecipes(response.data);
    } catch (error) {
      // Use cache as fallback
      if (recipesCache) {
        setRecipes(recipesCache);
      }
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const toggleRecipe = (recipeId) => {
    setSelectedRecipes(prev => 
      prev.includes(recipeId)
        ? prev.filter(id => id !== recipeId)
        : [...prev, recipeId]
    );
  };

  const handleNext = () => {
    if (selectedRecipes.length === 0) {
      toast.error(t('wizard.selectMin'));
      return;
    }
    navigate('/dry-run', { state: { selectedRecipes } });
  };

  if (loading) return <div className="loading-screen">{t('common.loading')}</div>;

  return (
    <div className="wizard-page" data-testid="wizard-page">
      <div className="wizard-container">
        <h1 className="wizard-title" data-testid="wizard-title">{t('wizard.title')}</h1>
        <p className="wizard-subtitle" data-testid="wizard-subtitle">{t('wizard.subtitle')}</p>

        {recipes.length === 0 ? (
          <Alert data-testid="no-recipes-alert">
            <AlertDescription>{t('wizard.norecipes')}</AlertDescription>
          </Alert>
        ) : (
          <div className="recipes-grid">
            {recipes.map(recipe => (
              <Card
                key={recipe.id}
                className={`recipe-card ${selectedRecipes.includes(recipe.id) ? 'selected' : ''}`}
                onClick={() => toggleRecipe(recipe.id)}
                data-testid={`recipe-card-${recipe.id}`}
              >
                <CardHeader>
                  <div className="recipe-header">
                    <CardTitle>{recipe.title}</CardTitle>
                    {selectedRecipes.includes(recipe.id) && (
                      <CheckCircle className="recipe-check" data-testid={`recipe-selected-${recipe.id}`} />
                    )}
                  </div>
                  <div className="recipe-badges">
                    <Badge variant="secondary">{recipe.version}</Badge>
                    <CategoryBadge category={recipe.category} />
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="recipe-card-description">{recipe.description}</CardDescription>
                  {recipe.steps && recipe.steps.length > 0 && (
                    <p className="recipe-info-text" data-testid={`recipe-info-${recipe.id}`}>
                      {recipe.steps[0].explain || `${recipe.steps.length} ${t('wizard.changes')}`}
                    </p>
                  )}
                  <div className="recipe-meta">
                    <span className="steps-count">{recipe.steps?.length || 0} {t('wizard.steps')}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {selectedRecipes.length > 0 && (
          <div className="wizard-actions">
            <Button onClick={handleNext} size="lg" data-testid="wizard-next-button">
              {t('wizard.next')} ({selectedRecipes.length})
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// Dry Run Page
const DryRunPage = () => {
  const [dryRunData, setDryRunData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedRecipes = location.state?.selectedRecipes || [];

  useEffect(() => {
    if (selectedRecipes.length === 0) {
      navigate('/wizard');
      return;
    }
    fetchDryRun();
  }, []);

  const fetchDryRun = async () => {
    try {
      const response = await axios.post(`${API}/profile/preview?lang=${i18n.language}`, {
        recipe_ids: selectedRecipes
      });
      setDryRunData(response.data);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    try {
      const response = await axios.post(`${API}/package/generate`, {
        recipe_ids: selectedRecipes,
        language: i18n.language,
        aggressiveness: 'balanced'
      });
      toast.success(t('common.success'));
      navigate('/download', { state: { token: response.data.token, expires_at: response.data.expires_at } });
    } catch (error) {
      const errorData = error.response?.data || {};
      
      if (error.response?.status === 429) {
        const retryAfter = errorData.retry_after;
        const message = retryAfter 
          ? `${errorData.message || t('toast.rateLimit.tooMany')} (${retryAfter}s)`
          : errorData.message || t('toast.rateLimit.daily');
        toast.error(message, { duration: 5000 });
      } else if (error.response?.status === 401) {
        toast.error(t('toast.session.relogin'));
        navigate('/login');
      } else {
        toast.error(errorData.message || t('common.error'));
      }
    }
  };

  if (loading) return <div className="loading-screen">{t('common.loading')}</div>;

  return (
    <div className="dryrun-page" data-testid="dryrun-page">
      <div className="dryrun-container">
        <h1 className="dryrun-title" data-testid="dryrun-title">{t('dryrun.title')}</h1>
        <p className="dryrun-subtitle" data-testid="dryrun-subtitle">{t('dryrun.subtitle')}</p>

        <div className="dryrun-summary">
          <Card>
            <CardContent className="summary-stats">
              <div className="stat" data-testid="dryrun-total-changes">
                <span className="stat-label">{t('dryrun.total')}</span>
                <span className="stat-value">{dryRunData?.total_changes || 0}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="dryrun-results">
          {dryRunData?.results.map((result, idx) => (
            <Card key={idx} className="result-card" data-testid={`result-card-${idx}`}>
              <CardHeader>
                <CardTitle>{result.title}</CardTitle>
                {result.description && (
                  <CardDescription className="result-description">
                    {result.description}
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <div className="result-changes">
                  <h4>{t('dryrun.changes')}</h4>
                  {result.changes.map((change, cidx) => (
                    <div key={cidx} className="change-item" data-testid={`change-item-${idx}-${cidx}`}>
                      <div className="change-desc">
                        {change.description}
                        {change.explain && (
                          <span className="change-explain">{change.explain}</span>
                        )}
                      </div>
                      <CategoryBadge category={change.category || 'komfort'} />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="dryrun-actions">
          <Button variant="outline" onClick={() => navigate('/wizard')} data-testid="dryrun-back-button">
            {t('dryrun.back')}
          </Button>
          <Button onClick={handleGenerate} size="lg" data-testid="dryrun-generate-button">
            {t('dryrun.generate')}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Code Preview Component
const CodePreview = ({ title, code, language = 'powershell' }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="code-preview" data-testid={`code-preview-${title}`}>
      <div className="code-preview-header">
        <div className="code-preview-title">
          <FileCode className="w-4 h-4" />
          <span>{title}</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          data-testid={`copy-${title}`}
        >
          {copied ? <CheckCheck className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        </Button>
      </div>
      <pre className="code-preview-content" data-testid={`code-content-${title}`}>
        <code>{code}</code>
      </pre>
    </div>
  );
};

// Download Page
const DownloadPage = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { token, expires_at } = location.state || {};
  const [downloading, setDownloading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [error, setError] = useState(null);
  const [copiedChecksum, setCopiedChecksum] = useState(null);

  useEffect(() => {
    if (token) {
      fetchPreview();
    }
  }, [token]);

  const fetchPreview = async () => {
    try {
      const res = await axios.get(`${API}/package/preview/${token}`);
      setPreview(res.data);
      setError(null);
    } catch (error) {
      console.error('Preview error:', error);
      const errorData = error.response?.data || {};
      
      if (error.response?.status === 401) {
        toast.error(t('toast.session.relogin'));
        navigate('/login', { state: { returnTo: '/download', token, expires_at } });
      } else if (error.response?.status === 403) {
        setError({
          type: 'forbidden',
          message: t('download.forbidden')
        });
      } else if (error.response?.status === 404 || error.response?.status === 410) {
        setError({
          type: 'expired',
          message: errorData.message || t('download.expired'),
          errorCode: errorData.error
        });
      } else {
        setError({
          type: 'network',
          message: errorData.message || t('download.previewFailed')
        });
      }
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleDownload = async () => {
    if (!token) {
      toast.error(t('toast.download.noToken'));
      return;
    }

    setDownloading(true);
    try {
      const res = await fetch(`${API}/package/download/${token}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ 
          error: 'unknown',
          message: t('download.downloadFailed')
        }));
        
        if (res.status === 401) {
          toast.error(t('toast.session.relogin'));
          navigate('/login');
        } else if (res.status === 404) {
          setError({
            type: 'expired',
            message: errorData.message || t('download.notFound'),
            errorCode: errorData.error
          });
        } else if (res.status === 410) {
          setError({
            type: 'expired',
            message: errorData.message || t('download.linkExpired'),
            errorCode: errorData.error
          });
        } else {
          toast.error(errorData.message || t('download.downloadFailed'));
        }
        return;
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      a.download = `CleanStart_${timestamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      toast.success(t('toast.download.success'));
    } catch (error) {
      console.error('Download error:', error);
      toast.error(t('toast.download.failed'));
    } finally {
      setDownloading(false);
    }
  };

  const copyChecksum = (filename, checksum) => {
    navigator.clipboard.writeText(checksum);
    setCopiedChecksum(filename);
    setTimeout(() => setCopiedChecksum(null), 2000);
  };

  if (!token) {
    return <Navigate to="/wizard" />;
  }

  // Error States
  if (error?.type === 'expired') {
    return (
      <div className="download-page" data-testid="download-page-expired">
        <div className="download-container">
          <Card className="error-card">
            <CardHeader>
              <div className="error-icon">
                <AlertTriangle size={48} />
              </div>
              <CardTitle>{t('download.notAvailable')}</CardTitle>
              <CardDescription>{error.message}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate('/wizard')} size="lg" data-testid="back-to-wizard">
                {t('download.backToWizard')}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error?.type === 'network') {
    return (
      <div className="download-page" data-testid="download-page-error">
        <div className="download-container">
          <Card className="error-card">
            <CardHeader>
              <div className="error-icon">
                <AlertTriangle size={48} />
              </div>
              <CardTitle>{t('download.networkError')}</CardTitle>
              <CardDescription>{error.message}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="error-actions">
                <Button onClick={fetchPreview} variant="outline" data-testid="retry-button">
                  {t('download.retry')}
                </Button>
                <Button onClick={() => navigate('/wizard')} data-testid="back-to-wizard">
                  {t('download.backToWizard')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="download-page" data-testid="download-page">
      <div className="download-container-wide">
        <h1 className="download-page-title">{t('download.title')}</h1>
        <p className="download-page-subtitle">{t('download.subtitle')}</p>

        {/* Download Action Card */}
        <Card className="download-action-card">
          <CardContent className="download-action-content">
            <div className="download-action-info">
              <Download size={32} />
              <div>
                <h3>{t('download.ready')}</h3>
                <p className="expires-text">
                  {t('download.expires')}: {new Date(expires_at).toLocaleString(i18n.language === 'de-CH' ? 'de-CH' : 'en-US')}
                </p>
              </div>
            </div>
            <Button 
              onClick={handleDownload} 
              size="lg" 
              data-testid="download-button"
              disabled={downloading}
            >
              {downloading ? (
                <>
                  <div className="spinner" />
                  {t('download.downloading')}
                </>
              ) : (
                <>
                  <Download className="w-5 h-5 mr-2" />
                  {t('download.button')}
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Schritt-für-Schritt-Anleitung */}
        <Card className="download-guide-card" data-testid="download-guide">
          <CardHeader>
            <CardTitle className="download-guide-title">
              <FileCode className="w-5 h-5" />
              {t('download.guide.title')}
            </CardTitle>
            <CardDescription>{t('download.guide.subtitle')}</CardDescription>
          </CardHeader>
          <CardContent>
            <ol className="download-guide-steps">
              <li><span className="step-num">1</span><span dangerouslySetInnerHTML={{ __html: t('download.guide.step1') }} /></li>
              <li><span className="step-num">2</span><span dangerouslySetInnerHTML={{ __html: t('download.guide.step2') }} /></li>
              <li><span className="step-num">3</span><span dangerouslySetInnerHTML={{ __html: t('download.guide.step3') }} /></li>
              <li><span className="step-num">4</span><span dangerouslySetInnerHTML={{ __html: t('download.guide.step4') }} /></li>
              <li className="step-highlight"><span className="step-num">5</span><span dangerouslySetInnerHTML={{ __html: t('download.guide.step5') }} /></li>
              <li><span className="step-num">6</span><span dangerouslySetInnerHTML={{ __html: t('download.guide.step6') }} /></li>
              <li><span className="step-num">7</span><span dangerouslySetInnerHTML={{ __html: t('download.guide.step7') }} /></li>
              <li><span className="step-num">8</span><span dangerouslySetInnerHTML={{ __html: t('download.guide.step8') }} /></li>
            </ol>
            <div className="download-guide-undo">
              <strong>{t('download.guide.undoTitle')}</strong>{' '}
              <span dangerouslySetInnerHTML={{ __html: t('download.guide.undoText') }} />
            </div>
          </CardContent>
        </Card>

        {loadingPreview ? (
          <div className="loading-preview">{t('download.previewLoading')}</div>
        ) : preview ? (
          <>
            {/* Code Previews */}
            <div className="preview-section">
              <h2 className="section-title">{t('download.codePreview')}</h2>
              <div className="preview-grid">
                <CodePreview
                  title="manifest.json"
                  code={JSON.stringify(preview.manifest, null, 2)}
                  language="json"
                />
                <CodePreview
                  title={`cleanstart.ps1 (${t('download.first50Lines')})`}
                  code={preview.cleanstart_ps1_preview}
                  language="powershell"
                />
              </div>
            </div>

            {/* Checksums */}
            <div className="preview-section">
              <h2 className="section-title">
                <Lock className="w-5 h-5" />
                {t('download.checksums')}
              </h2>
              <Card>
                <CardContent>
                  <div className="checksums-table">
                    <table>
                      <thead>
                        <tr>
                          <th>{t('download.filename')}</th>
                          <th>SHA256</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(preview.checksums).map(([filename, checksum]) => (
                          <tr key={filename} data-testid={`checksum-${filename}`}>
                            <td className="filename">{filename}</td>
                            <td className="checksum">{checksum}</td>
                            <td>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => copyChecksum(filename, checksum)}
                                data-testid={`copy-checksum-${filename}`}
                              >
                                {copiedChecksum === filename ? (
                                  <CheckCheck className="w-4 h-4" />
                                ) : (
                                  <Copy className="w-4 h-4" />
                                )}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Instructions */}
            <Alert className="download-instructions-alert">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>{i18n.language === 'de-CH' ? 'Wichtig:' : 'Important:'}</strong> {t('download.warning')}
              </AlertDescription>
            </Alert>
          </>
        ) : null}
      </div>
    </div>
  );
};

// Admin Page (Simplified)
const AdminPage = () => {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    fetchRecipes();
  }, []);

  const fetchRecipes = async () => {
    try {
      const response = await axios.get(`${API}/recipes/all/admin`);
      setRecipes(response.data);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading-screen">{t('common.loading')}</div>;

  return (
    <div className="admin-page" data-testid="admin-page">
      <div className="admin-container">
        <h1 className="admin-title" data-testid="admin-title">{t('admin.title')}</h1>
        <div className="recipes-list">
          {recipes.map(recipe => (
            <Card key={recipe.id} className="admin-recipe-card" data-testid={`admin-recipe-${recipe.id}`}>
              <CardHeader>
                <CardTitle>{recipe.title}</CardTitle>
                <Badge variant={recipe.published ? 'default' : 'secondary'}>
                  {recipe.published ? t('admin.published') : t('admin.draft')}
                </Badge>
              </CardHeader>
              <CardContent>
                <CardDescription>{recipe.description}</CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

// App Content - shows global LoadingScreen during initialization

// EULA Page
const EulaPage = () => {
  const { t } = useTranslation();
  const sections = [1, 2, 3, 4, 5, 6, 7];
  
  return (
    <div className="legal-page" data-testid="eula-page">
      <div className="legal-container">
        <h1 className="legal-title">{t('eula.title')}</h1>
        <p className="legal-updated">{t('eula.lastUpdated')}</p>
        <p className="legal-intro">{t('eula.intro')}</p>
        {sections.map(i => (
          <section key={i} className="legal-section">
            <h2>{t(`eula.section${i}.title`)}</h2>
            <p>{t(`eula.section${i}.text`)}</p>
          </section>
        ))}
      </div>
    </div>
  );
};

// Privacy Page
const PrivacyPage = () => {
  const { t } = useTranslation();
  const sections = [1, 2, 3, 4, 5, 6, 7, 8];
  
  return (
    <div className="legal-page" data-testid="privacy-page">
      <div className="legal-container">
        <h1 className="legal-title">{t('privacy.title')}</h1>
        <p className="legal-updated">{t('privacy.lastUpdated')}</p>
        <p className="legal-intro">{t('privacy.intro')}</p>
        {sections.map(i => (
          <section key={i} className="legal-section">
            <h2>{t(`privacy.section${i}.title`)}</h2>
            <p>{t(`privacy.section${i}.text`)}</p>
          </section>
        ))}
      </div>
    </div>
  );
};
const AppContent = () => {
  const { initializing, serverStatus } = useAuth();
  const { t } = useTranslation();

  if (initializing) {
    const message = serverStatus === 'starting'
      ? t('loading.serverStarting')
      : t('loading.default');
    const subtitle = serverStatus === 'starting'
      ? t('loading.serverHint')
      : null;

    return <LoadingScreen message={message} subtitle={subtitle} />;
  }

  return (
    <div className="App">
      <Navigation />
      <main className="main-content">
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verify" element={<VerifyPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/wizard" element={<ProtectedRoute><WizardPage /></ProtectedRoute>} />
          <Route path="/dry-run" element={<ProtectedRoute><DryRunPage /></ProtectedRoute>} />
          <Route path="/download" element={<ProtectedRoute><DownloadPage /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>} />
          <Route path="/eula" element={<EulaPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
